import mixpanel from 'mixpanel-browser';

import { getBrowserInfoForMixpanel } from '../../../../shared-utility/src/index';

const mixPanelConfig = {
  shouldUseMixPanel: false,
  isInitialized: false,
};

export const identifyUser = (identifyId, registerOptions = {}) => {
  if (!mixPanelConfig.isInitialized) {
    return;
  }

  mixpanel.identify(identifyId);
  mixpanel.register(registerOptions);
};

export const initMixPanel = ({ enableBrowserInfoTracking = true } = {}) => {
  mixPanelConfig.isInitialized = false;

  const MIXPANEL_PROJECT_TOKEN = process.env.NX_PUBLIC_MIXPANEL_PROJECT_TOKEN;

  if (MIXPANEL_PROJECT_TOKEN) {
    try {
      const mixpanelSettings = {
        persistence: 'localStorage',
        record_sessions_percent: 1, // Enable Session Replay for 1% of sessions
        record_heatmap_data: true, // Enable Heatmap data collection

        // Session Replay: unmask everything for analysis (text, inputs, images, video, canvas).
        // Sensitive inputs (password, email, tel, hidden) are always masked by the SDK.
        record_mask_all_text: false,
        record_mask_all_inputs: false,
        record_block_selector: '', // Block nothing — img, video, audio are recorded
        record_canvas: true, // Record canvas (camera/selfie/document)
      };

      mixpanel.init(MIXPANEL_PROJECT_TOKEN, mixpanelSettings);

      // Registra informações do browser como super properties
      // Essas propriedades serão enviadas automaticamente em todos os eventos
      if (enableBrowserInfoTracking) {
        const browserInfo = getBrowserInfoForMixpanel();
        mixpanel.register(browserInfo);
      }

      mixPanelConfig.isInitialized = true;
    } catch (error) {
      console.error('[MixPanel][initMixPanel]', error);
      mixPanelConfig.isInitialized = false;
    }
  }
  return mixPanelConfig.isInitialized;
};

export const pushToMixPanel = (dataLayer) => {
  if (!mixPanelConfig.isInitialized) {
    return;
  }

  mixpanel.track(dataLayer.event, {
    ...dataLayer,
  });
};

/*
 * Same event, but the caller learns whether it actually reached Mixpanel.
 *
 * `send_immediately` is what makes the answer real. The SDK batches by default
 * (`batch_requests: true`), and on that path the callback fires when the event
 * is ENQUEUED — it reports success even when the network is down. Bypassing the
 * batcher routes the callback through the transport, so `1`/`0` is the HTTP
 * result.
 *
 * Only for events the user is waiting on the result of. Everything else should
 * stay batched: one request per event is what batching exists to avoid.
 */
/*
 * The request is given a deadline, and the promise is given a separate one.
 *
 * `timeout_ms` is the real fix: the SDK sets no XHR timeout of its own, so a
 * stalled connection never reaches readyState 4, the callback never runs, and a
 * caller awaiting this sits in a loading state with no way out. It also aborts
 * the request instead of leaving the socket dangling, and the SDK answers it
 * through its own error branch as `callback(0)`.
 *
 * The guard behind it exists for the case `timeout_ms` cannot cover — the
 * callback never being invoked at all — so that this function's contract is
 * "always settles", not "settles if the SDK behaves".
 *
 * A send that times out and is then retried can arrive twice. That is accepted:
 * a duplicate report costs a support reader a moment, while a stuck screen
 * costs the report itself.
 */
const SEND_TIMEOUT_MS = 10000;
const SETTLE_GUARD_MS = SEND_TIMEOUT_MS + 2000;

export const pushToMixPanelImmediate = (dataLayer) =>
  new Promise((resolve) => {
    if (!mixPanelConfig.isInitialized) {
      resolve(false);
      return;
    }

    const guard = setTimeout(() => resolve(false), SETTLE_GUARD_MS);
    const settle = (value) => {
      clearTimeout(guard);
      resolve(value);
    };

    try {
      mixpanel.track(
        dataLayer.event,
        { ...dataLayer },
        { send_immediately: true, timeout_ms: SEND_TIMEOUT_MS },
        (status) => settle(status === 1),
      );
    } catch (error) {
      console.error('[MixPanel][pushToMixPanelImmediate]', error);
      settle(false);
    }
  });
